import { HelpIconTooltip } from '@gto/ui/components/help-icon-tooltip'
import type { DataTableColumnMeta } from '@gto/ui/data-table'

type ColumnWidthOptions = {
  /**
   * This column's cell stacks two lines (a partner name over their email). The wrapper reads it
   * off the columns and raises the table's row height once — the requirement travels with the
   * column instead of every page having to remember `density="comfortable"`.
   */
  stacked?: boolean
  /**
   * DataGridPro's `minWidth`. For a fluid column this is both the floor and the flex basis;
   * for a fixed one it is simply the width, since nothing grows it.
   */
  min: number
  /**
   * Share of the spare width relative to the other fluid columns — DataGridPro's `flex`,
   * carried over verbatim. Omit only for a genuinely equal-weight table: left out, every
   * fluid column takes the same share, which flattens a layout whose columns were authored
   * at different weights (a 220px name column beside a 90px status column).
   */
  flex?: number
  align?: DataTableColumnMeta['align']
  /** Align the header independently of the body (numeric columns often differ). */
  headerAlign?: DataTableColumnMeta['headerAlign']
}

/** The shape both factories share; they differ only in whether the column joins the flex pool. */
const column = ({ min, flex, stacked, align, headerAlign }: ColumnWidthOptions, grow: boolean) => ({
  size: min,
  minSize: min,
  enableResizing: false,
  meta: {
    ...(grow ? { grow: true, growFactor: flex } : {}),
    stacked,
    align,
    headerAlign,
  } satisfies DataTableColumnMeta,
})

/**
 * A width-filling column: the TanStack shape of DataGridPro's `flex` + `minWidth` pair.
 *
 * DataGridPro's `flex` split spare width in PROPORTION to each column's flex value. The DS
 * carries that across as `meta.growFactor` (the CSS `flex-grow`), so pass the ORIGINAL flex
 * number as `flex` and the layout keeps its authored weighting at every viewport. Leaving it
 * off is not neutral: every fluid column then takes an equal share, which at wide viewports
 * squeezes the wide text columns and bloats the narrow status ones until the table reads as
 * one evenly-spaced grid.
 *
 * `enableResizing: false` because the portal never let users resize columns, and a grow
 * column that also resizes fights the auto-fill.
 */
export const fluidColumn = (options: ColumnWidthOptions) => column(options, true)

/**
 * A fixed-width column — no `grow`, so it holds its width while the fluid columns absorb
 * the slack. For icon/action rails and short status cells.
 */
export const fixedColumn = (options: Omit<ColumnWidthOptions, 'flex'>) => column(options, false)

/**
 * A column `header` carrying an explanation — DataGridPro's `description`, which has no DS
 * column prop (the CONTROL exists, only the prop doesn't).
 *
 * The label stays plain header text so it inherits the header band's colour and truncation;
 * passing it as the tooltip's own `label` renders it `body3 text-secondary` inside the
 * header's truncating span, which reads lighter than its neighbours and loses the ellipsis.
 * Only the icon is the trigger.
 *
 * The click guard is the non-obvious half: the DS hangs the sort toggle on the columnheader
 * itself, so without it reaching for the hint re-sorts the column and re-queries the server.
 * Applied for every column rather than only today's sortable ones — whether a column sorts
 * is not something the next edit should have to remember.
 */
export const headerWithHint = (label: string, hint: string) => () => (
  <span
    className="inline-flex items-center gap-1"
    onClick={(event) => event.stopPropagation()}
  >
    {label}
    <HelpIconTooltip content={hint} />
  </span>
)
